import React, { useState } from 'react';

export const PatientStories = ({ onClose }) => {
  const [activeStory, setActiveStory] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const patients = [
    {
      id: 0,
      name: "Emma",
      age: 28,
      condition: "Cancer Survivor & Advocate",
      location: "San Francisco, CA",
      color: "#f97316",
      image: "/illustrations/maya.jpg",
      quote: "Sharing my story helped me heal in ways I never expected.",
      story: "When I was diagnosed with cancer at 24, I felt completely alone. The medical information was overwhelming, but what I really needed was to hear from someone who had been through it. That's why I joined Real Patients. Now, four years later, I've participated in 12 research studies and connected with hundreds of young adults starting their own journeys. Every survey I complete, every interview I give, I know I'm helping shape treatments that could save lives.",
      impact: "Contributed to 3 clinical trial designs",
      rewards: "$4,200"
    },
    {
      id: 1,
      name: "Marcus",
      age: 34,
      condition: "Living with Type 2 Diabetes",
      location: "Atlanta, GA",
      color: "#78716c",
      image: "/illustrations/marcus.jpg",
      quote: "My experience matters. Real Patients showed me that.",
      story: "I've been managing diabetes for 8 years. For most of that time, I felt like just another patient number. When I discovered Real Patients, everything changed. Researchers actually want to hear about my daily struggles, my wins, my frustrations with medication side effects. Last month, I was part of a focus group that directly influenced how a major pharma company is designing their patient support program. That's powerful.",
      impact: "Participated in 28 research activities",
      rewards: "$6,800"
    },
    {
      id: 2,
      name: "Maria",
      age: 32,
      condition: "Autoimmune Patient & Researcher",
      location: "Boston, MA",
      color: "#0d9488",
      image: "/illustrations/sarah.jpg",
      quote: "I'm both the scientist and the subject. That perspective is invaluable.",
      story: "As someone living with an autoimmune condition while working in healthcare, I have a unique perspective. I understand the science, but I also understand what it's like to wake up not knowing if today will be a good day or a flare day. Through Real Patients, I've been able to bridge both worlds. I've helped translate complex patient experiences into actionable research insights, and I've helped researchers understand what questions they should really be asking.",
      impact: "Advised on 5 research protocols",
      rewards: "$8,500"
    },
    {
      id: 3,
      name: "Alex",
      age: 26,
      condition: "Chronic Pain Advocate",
      location: "Denver, CO",
      color: "#115e59",
      image: "/illustrations/james.jpg",
      quote: "Living with chronic pain at a young age taught me to speak up.",
      story: "Chronic pain is invisible. People can't see it, so they often don't believe it—especially when you're young. I spent years feeling dismissed by doctors who thought I was too young to have 'real' pain. Real Patients gave me a platform where my experience is valued. I've spoken on advisory boards, contributed to pain management research, and connected with others who truly understand. For the first time, my pain has purpose.",
      impact: "Member of 2 patient advisory boards",
      rewards: "$5,100"
    },
    {
      id: 4,
      name: "Grace",
      age: 72,
      condition: "Patient Advocate & Mentor",
      location: "Honolulu, HI",
      color: "#eab308",
      image: "/illustrations/elena.jpg",
      quote: "After 50 years of living with chronic conditions, I have wisdom to share.",
      story: "I've lived through the evolution of healthcare—from a time when patients had no voice to today, where our experiences shape research. Through Real Patients, I mentor younger patients and share insights from decades of managing multiple conditions. Researchers value my long-term perspective on treatments, side effects, and what truly matters for quality of life. My story isn't just about survival—it's about thriving.",
      impact: "Mentored 24 new patient advocates",
      rewards: "$7,200"
    }
  ];

  const handleStoryClick = (patient) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setActiveStory(patient);
      setIsTransitioning(false);
    }, 300);
  };

  const handleCloseStory = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setActiveStory(null);
      setIsTransitioning(false);
    }, 300);
  };

  // Individual story view
  if (activeStory) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: activeStory.color,
          zIndex: 2000,
          overflow: 'auto',
          opacity: isTransitioning ? 0 : 1,
          transition: 'opacity 0.3s ease'
        }}
      >
        <style>
          {`
            @keyframes fadeSlideUp {
              from { opacity: 0; transform: translateY(30px); }
              to { opacity: 1; transform: translateY(0); }
            }
            .story-animate {
              animation: fadeSlideUp 0.6s ease forwards;
            }
            .story-animate-delay-1 { animation-delay: 0.1s; opacity: 0; }
            .story-animate-delay-2 { animation-delay: 0.2s; opacity: 0; }
            .story-animate-delay-3 { animation-delay: 0.3s; opacity: 0; }
            .story-animate-delay-4 { animation-delay: 0.4s; opacity: 0; }
          `}
        </style>

        {/* Close button */}
        <button
          onClick={handleCloseStory}
          style={{
            position: 'fixed',
            top: '30px',
            right: '30px',
            width: '50px',
            height: '50px',
            borderRadius: '50%',
            border: 'none',
            backgroundColor: 'rgba(255,255,255,0.2)',
            color: 'white',
            fontSize: '24px',
            cursor: 'pointer',
            zIndex: 2001,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          ×
        </button>

        <div
          className="story-detail-container"
          style={{
            maxWidth: '900px',
            margin: '0 auto',
            padding: '80px 40px',
            color: 'white'
          }}
        >
          {/* Quote */}
          <div className="story-animate" style={{ marginBottom: '60px' }}>
            <p
              style={{
                fontFamily: '"Source Serif Pro", Georgia, serif',
                fontSize: 'clamp(32px, 5vw, 56px)',
                fontWeight: '400',
                fontStyle: 'italic',
                lineHeight: '1.3',
                margin: 0
              }}
            >
              "{activeStory.quote}"
            </p>
          </div>

          {/* Name and details with photo */}
          <div className="story-animate story-animate-delay-1" style={{ marginBottom: '50px', display: 'flex', alignItems: 'center', gap: '24px' }}>
            {/* Large patient photo */}
            <div
              style={{
                width: '100px',
                height: '100px',
                borderRadius: '50%',
                overflow: 'hidden',
                border: '4px solid rgba(255,255,255,0.3)',
                flexShrink: 0
              }}
            >
              <img
                src={activeStory.image}
                alt={activeStory.name}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            </div>
            <div>
              <h1
                style={{
                  fontFamily: '"Source Serif Pro", Georgia, serif',
                  fontSize: '36px',
                  fontWeight: '700',
                  margin: '0 0 8px 0'
                }}
              >
                {activeStory.name}
              </h1>
              <p style={{ fontSize: '18px', opacity: 0.85, margin: 0 }}>
                {activeStory.age} • {activeStory.condition} • {activeStory.location}
              </p>
            </div>
          </div>

          {/* Story */}
          <div className="story-animate story-animate-delay-2" style={{ marginBottom: '60px' }}>
            <p
              style={{
                fontSize: '20px',
                lineHeight: '1.8',
                opacity: 0.95,
                margin: 0
              }}
            >
              {activeStory.story}
            </p>
          </div>

          {/* Impact stats */}
          <div
            className="story-animate story-animate-delay-3"
            style={{
              display: 'flex',
              gap: '40px',
              flexWrap: 'wrap'
            }}
          >
            <div
              style={{
                backgroundColor: 'rgba(255,255,255,0.15)',
                padding: '30px 40px',
                borderRadius: '16px',
                flex: '1',
                minWidth: '200px'
              }}
            >
              <p style={{ fontSize: '14px', textTransform: 'uppercase', letterSpacing: '2px', opacity: 0.7, margin: '0 0 8px 0' }}>
                Impact
              </p>
              <p style={{ fontSize: '22px', fontWeight: '600', margin: 0 }}>
                {activeStory.impact}
              </p>
            </div>
            <div
              style={{
                backgroundColor: 'rgba(255,255,255,0.15)',
                padding: '30px 40px',
                borderRadius: '16px',
                flex: '1',
                minWidth: '200px'
              }}
            >
              <p style={{ fontSize: '14px', textTransform: 'uppercase', letterSpacing: '2px', opacity: 0.7, margin: '0 0 8px 0' }}>
                Rewarded
              </p>
              <p style={{ fontSize: '22px', fontWeight: '600', margin: 0 }}>
                {activeStory.rewards}
              </p>
            </div>
          </div>

          {/* Navigation hint */}
          <div
            className="story-animate story-animate-delay-4"
            style={{
              marginTop: '80px',
              textAlign: 'center'
            }}
          >
            <button
              onClick={handleCloseStory}
              style={{
                backgroundColor: 'white',
                color: activeStory.color,
                border: 'none',
                padding: '16px 40px',
                fontSize: '16px',
                fontWeight: '600',
                borderRadius: '30px',
                cursor: 'pointer'
              }}
            >
              ← Back to all stories
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main stories grid
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#f7f5f3',
        zIndex: 2000,
        overflow: 'auto',
        opacity: isTransitioning ? 0 : 1,
        transition: 'opacity 0.3s ease'
      }}
    >
      <style>
        {`
          @keyframes staggerIn {
            from { opacity: 0; transform: translateY(40px) scale(0.95); }
            to { opacity: 1; transform: translateY(0) scale(1); }
          }
          .card-stagger {
            animation: staggerIn 0.5s ease forwards;
          }
          .card-0 { animation-delay: 0.1s; opacity: 0; }
          .card-1 { animation-delay: 0.2s; opacity: 0; }
          .card-2 { animation-delay: 0.3s; opacity: 0; }
          .card-3 { animation-delay: 0.4s; opacity: 0; }
          .card-4 { animation-delay: 0.5s; opacity: 0; }
          .story-card:hover {
            transform: translateY(-8px) scale(1.02);
            box-shadow: 0 20px 60px rgba(0,0,0,0.2);
          }
          @media (max-width: 600px) {
            .stories-grid-container {
              padding: 60px 20px !important;
            }
            .stories-grid {
              grid-template-columns: 1fr !important;
            }
            .story-card {
              min-height: 280px !important;
              grid-row: span 1 !important;
            }
          }
          @media (max-width: 480px) {
            .story-detail-container {
              padding: 60px 20px !important;
            }
          }
        `}
      </style>

      {/* Close button */}
      <button
        onClick={onClose}
        style={{
          position: 'fixed',
          top: '30px',
          right: '30px',
          width: '50px',
          height: '50px',
          borderRadius: '50%',
          border: '2px solid #272726',
          backgroundColor: 'white',
          color: '#272726',
          fontSize: '24px',
          cursor: 'pointer',
          zIndex: 2001,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        ×
      </button>

      <div className="stories-grid-container" style={{ padding: '80px 40px', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '60px' }}>
          <h1
            style={{
              fontFamily: '"Source Serif Pro", Georgia, serif',
              fontSize: 'clamp(40px, 6vw, 72px)',
              fontWeight: '700',
              color: '#272726',
              margin: '0 0 20px 0'
            }}
          >
            Meet Our Patients
          </h1>
          <p
            style={{
              fontSize: '20px',
              color: '#666',
              maxWidth: '600px',
              margin: '0 auto',
              lineHeight: '1.6'
            }}
          >
            Real people. Real stories. Real impact on healthcare.
          </p>
        </div>

        {/* Stories Grid - Bento style */}
        <div
          className="stories-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '24px',
            maxWidth: '1200px',
            margin: '0 auto'
          }}
        >
          {patients.map((patient, index) => (
            <div
              key={patient.id}
              className={`story-card card-stagger card-${index}`}
              onClick={() => handleStoryClick(patient)}
              style={{
                backgroundColor: patient.color,
                borderRadius: '24px',
                padding: '40px',
                cursor: 'pointer',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                display: 'flex',
                flexDirection: 'column',
                minHeight: index === 0 || index === 2 ? '400px' : '320px',
                gridRow: index === 0 ? 'span 2' : 'span 1',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              {/* Decorative circle */}
              <div
                style={{
                  position: 'absolute',
                  top: '-50px',
                  right: '-50px',
                  width: '200px',
                  height: '200px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  pointerEvents: 'none'
                }}
              />

              <div style={{ flex: 1 }}>
                <p
                  style={{
                    fontFamily: '"Source Serif Pro", Georgia, serif',
                    fontSize: index === 0 || index === 2 ? '28px' : '22px',
                    fontWeight: '400',
                    fontStyle: 'italic',
                    color: 'white',
                    lineHeight: '1.4',
                    margin: '0 0 auto 0'
                  }}
                >
                  "{patient.quote}"
                </p>
              </div>

              <div style={{ marginTop: '30px', display: 'flex', alignItems: 'center', gap: '16px' }}>
                {/* Patient Photo */}
                <div
                  style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    border: '3px solid rgba(255,255,255,0.3)',
                    flexShrink: 0
                  }}
                >
                  <img
                    src={patient.image}
                    alt={patient.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                </div>
                <div>
                  <p
                    style={{
                      fontSize: '20px',
                      fontWeight: '700',
                      color: 'white',
                      margin: '0 0 4px 0'
                    }}
                  >
                    {patient.name}
                  </p>
                  <p
                    style={{
                      fontSize: '14px',
                      color: 'rgba(255,255,255,0.8)',
                      margin: 0
                    }}
                  >
                    {patient.condition}
                  </p>
                </div>
              </div>

              {/* Read more hint */}
              <div
                style={{
                  position: 'absolute',
                  bottom: '20px',
                  right: '20px',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '20px'
                }}
              >
                →
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
